import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import ScrollReveal from "@/components/ScrollReveal";

const FAQ = {
  bg: {
    tag: "06 — ЧЕСТО ЗАДАВАНИ ВЪПРОСИ",
    heading: "Това, което другите се страхуват да обяснят",
    items: [
      {
        q: "Колко време отнема да видя реални резултати?",
        a: "Първите данни (CTR, engagement, ranking shifts) се появяват в рамките на 2–4 седмици. Сериозни конверсии — 30 до 60 дни. SEO ефекти — 90+ дни. Без трикове, без обещания за overnight magic.",
      },
      {
        q: "Колко струва пакет средно?",
        a: "Стартер пакетите тръгват от ~890 €/мес, Професионал между 2 400 и 4 800 €/мес, Ентерпрайз се определя по обем (от 8 000+ €/мес). Всичко прозрачно, без скрити такси.",
      },
      {
        q: "Имате ли минимум договор?",
        a: "Не. Работим на месечна основа с 30-дневно предизвестие. Ако не работим — спираш, без неустойки. Защото знаем, че работим.",
      },
      {
        q: "Кой реално ще работи по моя проект?",
        a: "Дедикиран екип от 2–4 души: стратег, performance specialist, дизайнер и/или копирайтър. Никакво outsourcing към freelance армия — всички са вътрешни.",
      },
      {
        q: "Какво се случва ако кампанията не работи?",
        a: "Първо: оптимизираме без допълнително заплащане. Второ: ако след 60 дни няма движение към ясно дефинираните KPI, изготвяме transition plan и прекратяваме без unwinding такси.",
      },
      {
        q: "Работите ли с компании извън България?",
        a: "Да. Около 30% от нашите клиенти са от EU, UK и US. Имаме целия процес и репортинг на английски. Multi-currency invoicing.",
      },
    ],
  },
  en: {
    tag: "06 — FAQ",
    heading: "What other agencies are afraid to explain",
    items: [
      {
        q: "How long until I see real results?",
        a: "First data (CTR, engagement, ranking shifts) shows up in 2–4 weeks. Serious conversions — 30 to 60 days. SEO compounding — 90+ days. No tricks, no overnight magic.",
      },
      {
        q: "What does an average package cost?",
        a: "Starter packages from ~€450/mo, Professional €1,200–€2,500/mo, Enterprise scoped by scale (€4,000+/mo). All transparent, no hidden fees.",
      },
      {
        q: "Is there a minimum contract?",
        a: "No. We work month-to-month with 30-day notice. If it doesn't work — you walk, no penalties. Because we know it'll work.",
      },
      {
        q: "Who actually works on my project?",
        a: "A dedicated team of 2–4 people: strategist, performance specialist, designer and/or copywriter. No outsourcing to a freelance army — everyone is in-house.",
      },
      {
        q: "What if the campaign doesn't perform?",
        a: "First: we optimize at no extra cost. Second: if after 60 days we're not moving toward clearly defined KPIs, we build a transition plan and end engagement without unwinding fees.",
      },
      {
        q: "Do you work with companies outside Bulgaria?",
        a: "Yes. ~30% of our clients are from EU, UK, and US. Full process and reporting in English. Multi-currency invoicing.",
      },
    ],
  },
};

const FaqSection = () => {
  const { lang } = useLang();
  const c = FAQ[lang];
  const [open, setOpen] = useState(0);

  return (
    <section data-testid="faq-section" className="relative py-28 md:py-36 border-b border-brand-border overflow-hidden">
      <div className="absolute inset-0 hex-grid opacity-30 pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="text-center mb-16">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
            {c.tag}
          </p>
          <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter">
            {c.heading.split(" ").slice(0, -2).join(" ")}{" "}
            <span className="text-white">{c.heading.split(" ").slice(-2).join(" ")}</span>
          </h2>
        </div>
        <ul className="max-w-3xl mx-auto divide-y divide-brand-border border-y border-brand-border">
          {c.items.map((it, i) => {
            const active = open === i;
            return (
              <ScrollReveal as="li" key={i} delay={i * 50}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(active ? -1 : i)}
                  aria-expanded={active}
                  className="w-full text-left py-6 md:py-8 flex items-start gap-6 group"
                >
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-purpleLight pt-2">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 font-heading text-xl md:text-2xl font-bold tracking-tight pr-4 text-white group-hover:text-brand-purpleLight transition-colors">
                    {it.q}
                  </span>
                  <span className={`mt-1 w-10 h-10 grid place-items-center rounded-full border ${active ? "border-brand-pink bg-brand-pink/10 text-brand-pink" : "border-brand-border text-zinc-300 group-hover:border-brand-purple"} transition-colors shrink-0`}>
                    {active ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ${active ? "max-h-96 opacity-100 pb-6 md:pb-8" : "max-h-0 opacity-0"}`}
                >
                  <p className="pl-14 md:pl-16 pr-12 text-zinc-300 leading-relaxed text-sm md:text-base">
                    {it.a}
                  </p>
                </div>
              </ScrollReveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default FaqSection;
